// src/pages/Profile.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { registerChartComponents } from "../utils/chartUtils";
import LoadingSpinner from "../components/LoadingSpinner";
import { ErrorMessage } from "../components/ErrorMessage";
import { ConsumptionChart } from "../components/ConsumptionChart";
import { MonthlyReadingChart } from "../components/MonthlyReadingChart";
import {
  fetchBuildingList,
  fetchBuildingData,
  fetchBuildingMonthlyData,
  fetchBuildingDailyData,
} from "../services/buildingService";
import { parseDate, getDateRange, prepareChartData } from "../utils/dataUtils";
import BuildingSelector from "../components/BuildingSelector";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { DailyReadingChart } from "../components/DailyReadingChart";
import { ErrorPopup } from "../components/ErrorPopup";
import ValveControl from "../components/ValveControl";
import ImageModal from "../components/ImageModal";
import { fetchImageByGuid } from "../services/imageService";

registerChartComponents();

/**
 * Profile component for displaying and managing building consumption data.
 * Handles building selection, data visualization, and export functionality.
 *
 * @component
 * @returns {JSX.Element} The rendered Profile component
 */
const Profile = () => {
  const { accounts } = useMsal();
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [monthlyChartData, setMonthlyChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buildingInfo, setBuildingInfo] = useState(null);
  const [buildingCount, setBuildingCount] = useState(0);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const { t } = useTranslation();
  // Determine if user is Valve Operator
  const roles = accounts?.[0]?.idTokenClaims?.roles || [];
  const isValveOperator = roles.some(
    (r) =>
      typeof r === "string" &&
      (r.toLowerCase() === "valveoperator" ||
        r.toLowerCase() === "valve operator")
  );
  const [currentReading, setCurrentReading] = useState(null);
  const [showMonthlyChart, setShowMonthlyChart] = useState(false);
  const [dailyChartData, setDailyChartData] = useState(null);
  const [showDailyChart, setShowDailyChart] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  
  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [imageTimestamp, setImageTimestamp] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageAbortController, setImageAbortController] = useState(null);

  // For valve operation, use the meter GUID (first meter by default)
  const controlGuid = buildingInfo?.meterGuid || null;

  /**
   * Loads and processes building consumption data for a selected building.
   * Fetches both daily and monthly consumption data, and prepares it for chart display.
   *
   * @param {Object} building - The building object to load data for
   * @param {string} building.Name - Building name
   * @param {string} building.Address - Building address
   * @param {Array} building.Meters - Array of meter objects
   * @param {string} building.BGuid - Building GUID for multiple meters
   */
  const loadBuildingData = useCallback(
    async (building) => {
      try {
        setLoading(true);
        setError(null);
        setShowErrorPopup(false);
        // Clear all chart data states when loading new building
        setChartData(null);
        setMonthlyChartData(null);
        setDailyChartData(null);
        setCurrentReading(null);
        setBuildingInfo(null);

        if (!building.Meters || building.Meters.length === 0) {
          throw new Error(t("no_meters"));
        }

        if (!building.Meters[0]?.Guid) {
          throw new Error(t("invalid_meter"));
        }

        setBuildingInfo({
          name: building.Name,
          address: building.Address,
          meterName: building.Meters[0].Name,
          meterGuid: building.Meters[0].Guid,
        });

        /*   const choosenGuid = () => {
          if (building.Meters.length > 1) {
            return building.BGuid;
          } else {
            return building.Meters[0].Guid;
          }
        }; */
        const guid = building.BGuid;
        const dateRange = getDateRange();

        const now = new Date();

        // Adjust for timezone offset
        const currentStart = new Date(now);
        currentStart.setDate(currentStart.getDate() - 1); // Go back one day
        currentStart.setHours(0, 0, 0, 0); // Set to midnight

        // Add timezone offset to ensure midnight in local time
        const timezoneOffset = currentStart.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
        currentStart.setTime(currentStart.getTime() - timezoneOffset);

        const currentEnd = new Date(); // Current date and time

        // Fetch current day's data
        const currentDayData = await fetchBuildingData(
          guid,
          currentStart,
          currentEnd
        );

        // Set current reading from the latest data point
        if (currentDayData?.DataList?.length > 0) {
          const latestReading =
            currentDayData.DataList[currentDayData.DataList.length - 1];
          setCurrentReading(latestReading.R || 0);
        }

        const data = await fetchBuildingData(
          guid,
          dateRange.startDate,
          dateRange.endDate
        );

        if (data?.DataList?.length > 0) {
          const chartData = data.DataList.map((point) => ({
            Aver30: point.C || 0,
            Limit: point.L || 0,
            Date: parseDate(point.T),
          })).sort((a, b) => a.Date - b.Date);

          setChartData(prepareChartData(chartData));
        } else {
          // Clear chart data when no data is available
          setChartData(null);
          setError(t("no_data_points"));
          setShowErrorPopup(true);
          return; // Exit early to prevent loading monthly/daily data
        }

        const monthlyData = await fetchBuildingMonthlyData(guid);
        if (monthlyData?.DataList?.length > 0) {
          // First, sort the data chronologically
          const sortedData = monthlyData.DataList.sort(
            (a, b) => new Date(a.T) - new Date(b.T)
          ).map((point) => ({
            Reading: parseFloat(point.R) || 0,
            Date: new Date(point.T),
          }));

          // Calculate differences between consecutive months
          const monthlyDifferences = sortedData
            .map((point, index) => {
              if (index === 0) return null; // Skip first entry as it has no previous reading
              const previousReading = sortedData[index - 1].Reading;
              const currentReading = point.Reading;
              const difference = currentReading - previousReading;

              return {
                Difference: difference.toFixed(2), // Round to 2 decimal places
                Date: point.Date.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                }),
              };
            })
            .filter((item) => item !== null); // Remove the first null entry

          const barChartData = {
            labels: monthlyDifferences.map((item) => item.Date),
            datasets: [
              {
                label: t("monthly_consumption_difference"),
                data: monthlyDifferences.map((item) =>
                  parseFloat(item.Difference)
                ),
                backgroundColor: "#cabb78",
                borderColor: "#b5a666",
                borderWidth: 1,
              },
            ],
          };

          setMonthlyChartData(barChartData);
        } else {
          setError(t("no_monthly_data"));
          setShowErrorPopup(true);
        }

        // After fetching monthly data, fetch daily data
        const dailyData = await fetchBuildingDailyData(guid);
        if (dailyData?.DataList?.length > 0) {
          // Sort the data chronologically
          const sortedData = dailyData.DataList.sort(
            (a, b) => new Date(a.T) - new Date(b.T)
          ).map((point) => ({
            Reading: parseFloat(point.R) || 0,
            Date: new Date(point.T),
          }));

          // Calculate differences between consecutive days
          const dailyDifferences = sortedData
            .map((point, index) => {
              if (index === 0) return null;
              const previousReading = sortedData[index - 1].Reading;
              const currentReading = point.Reading;
              const difference = currentReading - previousReading;

              return {
                Difference: difference.toFixed(2),
                Date: point.Date.toISOString().split("T")[0], // Format as YYYY-MM-DD
              };
            })
            .filter((item) => item !== null);

          const barChartData = {
            labels: dailyDifferences.map((item) => item.Date),
            datasets: [
              {
                label: t("daily_consumption_difference"),
                data: dailyDifferences.map((item) =>
                  parseFloat(item.Difference)
                ),
                backgroundColor: "#cabb78",
                borderColor: "#b5a666",
                borderWidth: 1,
              },
            ],
          };

          setDailyChartData(barChartData);
        }
      } catch (err) {
        console.error("Error:", err);
        setError(err.message);
        setShowErrorPopup(true);
        // Clear all chart data on error
        setChartData(null);
        setMonthlyChartData(null);
        setDailyChartData(null);
        setCurrentReading(null);
        setBuildingInfo(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  /**
   * Initial data loading effect that fetches building list and sets up initial building selection.
   * Automatically loads data for single building scenarios.
   */
  useEffect(() => {
    const loadData = async () => {
      if (!accounts[0]?.username) {
        setError(t("no_user_account"));
        return;
      }

      try {
        setLoading(true);
        const buildingData = await fetchBuildingList(accounts[0].username);

        if (!buildingData || buildingData.length === 0) {
          setError(t("no_buildings"));
          return;
        }

        // Sort buildings alphabetically by name
        const sortedBuildings = buildingData.sort((a, b) =>
          a.Name.localeCompare(b.Name, undefined, { sensitivity: "base" })
        );

        setBuildings(sortedBuildings);
        setBuildingCount(sortedBuildings.length);

        // Remove automatic building selection
        // Only load data for single building case
        if (sortedBuildings.length === 1) {
          setSelectedBuilding(sortedBuildings[0]);
          await loadBuildingData(sortedBuildings[0]);
        }
        // Remove the automatic selection of first building for multiple buildings
      } catch (err) {
        console.error("Error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [accounts, loadBuildingData, t]);

  /**
   * Effect to load building data when selected building changes
   */
  useEffect(() => {
    if (selectedBuilding) {
      loadBuildingData(selectedBuilding);
    }
  }, [selectedBuilding, loadBuildingData]);

  /**
   * Handles building selection from the dropdown and loads its data
   *
   * @param {Object} building - The selected building object
   */
  const handleBuildingSelect = async (building) => {
    try {
      if (building) {
        setSelectedBuilding(building);
        await loadBuildingData(building);
      } else {
        throw new Error("No building data available");
      }
    } catch (error) {
      console.error("Building selection error:", error);
      setError(error.message);
    }
  };

  /**
   * Resets the consumption chart zoom level to default view
   */
  const handleReset = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  /**
   * Handles date range changes for the consumption chart
   *
   * @param {string} type - The type of date change ('range')
   * @param {Object} value - The date range object
   * @param {Date} value.start - Start date
   * @param {Date} value.end - End date
   */
  const handleDateChange = async (type, value) => {
    if (type === "range") {
      try {
        // Create Date objects
        const startDate = new Date(value.start);
        const endDate = new Date(value.end);
        const tzOffset = startDate.getTimezoneOffset();

        // Set start date to beginning of the selected day (00:00:00)
        const adjustedStartDate = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0,
          0,
          0,
          0
        );

        // Set end date to beginning of the next day (00:00:00)
        const adjustedEndDate = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate() + 1, // Add 1 day to get to next day at 00:00
          0,
          0,
          0,
          0
        );

        // Apply timezone adjustments and convert to ISO string
        const finalStartDate = new Date(
          adjustedStartDate.getTime() - tzOffset * 60000
        )
          .toISOString()
          .split(".")[0];
        const finalEndDate = new Date(
          adjustedEndDate.getTime() - tzOffset * 60000
        )
          .toISOString()
          .split(".")[0];

        const building = Array.isArray(selectedBuilding)
          ? selectedBuilding[0]
          : selectedBuilding;

        if (!building) {
          throw new Error(t("building_data_unavailable"));
        }

        const guid = building.BGuid;

        // Fetch new data from API with the selected date range
        const data = await fetchBuildingData(
          guid,
          finalStartDate,
          finalEndDate
        );

        if (data?.DataList?.length > 0) {
          const chartData = data.DataList.map((point) => ({
            Aver30: point.C || 0,
            Limit: point.L || 0,
            Date: parseDate(point.T),
          })).sort((a, b) => a.Date - b.Date);

          setChartData(prepareChartData(chartData));

          // Update chart directly if needed
          if (chartRef.current) {
            chartRef.current.update();
          }
        } else {
          setError(t("no_data_available"));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message || t("fetch_data_error"));
      }
    }
  };

  const handleToggleChart = () => {
    if (showMonthlyChart || showDailyChart) {
      setShowMonthlyChart(false);
      setShowDailyChart(false);
    } else {
      setShowMonthlyChart(true);
    }
  };

  const handleToggleDailyChart = () => {
    setShowDailyChart(true);
    setShowMonthlyChart(false);
  };

  const handleDownloadExcel = () => {
    if (!monthlyChartData || !monthlyChartData.labels) {
      setError(t("no_data_download"));
      return;
    }

    // Create worksheet data
    const wsData = monthlyChartData.labels.map((label, index) => ({
      Month: label,
      Consumption: monthlyChartData.datasets[0].data[index],
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(wsData);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("monthly_reading"));

    // Generate Excel file
    XLSX.writeFile(
      wb,
      `${buildingInfo?.name || "building"}_monthly_consumption.xlsx`
    );
  };

  const handleCloseError = () => {
    setShowErrorPopup(false);
    setError(null);
  };

  /**
   * Handles opening the image modal and fetching the image
   */
  const handleOpenImageModal = async () => {
    if (!buildingInfo?.meterGuid) {
      setError("No meter GUID available");
      return;
    }

    try {
      setImageLoading(true);
      setShowImageModal(true);
      setImageData(null);
      setImageTimestamp(null);

      // Create abort controller for this request
      const abortController = new AbortController();
      setImageAbortController(abortController);

      const { imageData: fetchedImageData, timestamp } = await fetchImageByGuid(
        buildingInfo.meterGuid,
        abortController.signal
      );

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      setImageData(fetchedImageData);
      setImageTimestamp(timestamp);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      setError(error.message || "Failed to load image");
      setShowImageModal(false);
    } finally {
      setImageLoading(false);
      setImageAbortController(null);
    }
  };

  /**
   * Handles closing the image modal
   */
  const handleCloseImageModal = () => {
    // Cancel any ongoing image fetch
    if (imageAbortController) {
      imageAbortController.abort();
      setImageAbortController(null);
    }
    setShowImageModal(false);
    setImageData(null);
    setImageTimestamp(null);
    setImageLoading(false);
  };

  return (
    <div className="min-h-screen pt-8 pb-4 overflow-y-auto">
      {showErrorPopup && error && (
        <ErrorPopup message={error} onClose={handleCloseError} />
      )}
      <div className="max-w-7xl mx-auto px-1 sm:px-2">
        <div className="w-full px-1 md:px-2 max-w-7xl mx-auto space-y-2">
          {/* Building Selector Section - Always show if multiple buildings */}
          {buildingCount > 1 && (
            <div className="bg-white rounded-lg shadow-lg p-1.5 transition-all duration-300 hover:shadow-xl h-[40px]">
              <BuildingSelector
                buildingData={buildings}
                selectedBuilding={selectedBuilding?.[0]}
                onBuildingSelect={handleBuildingSelect}
                className="w-full h-7"
              />
            </div>
          )}

          {/* Show loading spinner or error message */}
          {loading && <LoadingSpinner />}
          {error && !showErrorPopup && <ErrorMessage message={error} />}

          {/* Building Information Card */}
          {!loading && buildingInfo && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-[#cabb78] to-[#b5a666] px-2 py-1.5">
                <h2 className="text-sm font-bold text-white">
                  {buildingInfo.name}
                </h2>
              </div>
              <div className="p-1.5 space-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  <div className="space-y-0.5">
                    <div className="text-xs text-gray-700 font-medium">
                      {buildingInfo.address}
                    </div>
                    {/* Enhanced styling for current reading */}
                    {currentReading !== null && (
                      <div className="mt-2 p-2 bg-gradient-to-r from-[#f8f4e3] to-[#f5f1dd] rounded-lg shadow-sm">
                        <div className="text-xs text-gray-600 font-medium mb-1">
                          {t("current_reading")}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-baseline">
                            <span className="text-2xl font-bold text-[#b5a666]">
                              {Math.trunc(currentReading)}
                            </span>
                            <span className="ml-1 text-sm font-medium text-gray-600">
                              m³
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date().toLocaleString()}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Valve Control */}
                    {isValveOperator && controlGuid && (
                      <div className="mt-2">
                        <ValveControl guid={controlGuid} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {(chartData || monthlyChartData || dailyChartData) && (
            <div className="w-full bg-white rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-1 space-y-1 sm:space-y-0 p-0.5">
                <h2 className="text-sm font-semibold text-gray-800">
                  {showMonthlyChart
                    ? t("monthly_reading")
                    : showDailyChart
                    ? t("daily_reading")
                    : t("consumption_overview")}
                </h2>
                <div className="flex gap-1">
                  <button
                    onClick={handleToggleChart}
                    className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded text-white bg-gradient-to-r from-[#cabb78] to-[#b5a666] hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    {showMonthlyChart || showDailyChart
                      ? t("show_daily")
                      : t("show_monthly_data")}
                  </button>
                  {dailyChartData && (
                    <button
                      onClick={handleToggleDailyChart}
                      className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded text-white bg-gradient-to-r from-[#cabb78] to-[#b5a666] hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      {t("show_daily_data")}
                    </button>
                  )}
                  {buildingInfo?.meterGuid && (
                    <button
                      onClick={handleOpenImageModal}
                      disabled={imageLoading}
                      className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded text-white bg-gradient-to-r from-[#cabb78] to-[#b5a666] hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {imageLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t("loading")}
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t("view_image")}
                        </>
                      )}
                    </button>
                  )}
                  {!showMonthlyChart && !showDailyChart && (
                    <button
                      onClick={handleReset}
                      className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded text-white bg-gradient-to-r from-[#cabb78] to-[#b5a666] hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      {t("reset_view")}
                    </button>
                  )}
                  {(showMonthlyChart || showDailyChart) && (
                    <button
                      onClick={handleDownloadExcel}
                      className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded text-white bg-gradient-to-r from-[#cabb78] to-[#b5a666] hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 shadow-sm hover:shadow-md"
                    >
                      {t("download_excel")}
                    </button>
                  )}
                </div>
              </div>
              <div className="relative h-[300px] sm:h-[400px]">
                {!showMonthlyChart && !showDailyChart && chartData && (
                  <ConsumptionChart
                    data={chartData}
                    chartRef={chartRef}
                    onReset={handleReset}
                    onDateChange={handleDateChange}
                  />
                )}
                {showMonthlyChart && monthlyChartData && (
                  <MonthlyReadingChart
                    data={monthlyChartData}
                    onToggleChart={handleToggleChart}
                  />
                )}
                {showDailyChart && dailyChartData && (
                  <DailyReadingChart
                    data={dailyChartData}
                    onToggleChart={handleToggleChart}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Image Modal */}
      <ImageModal
        isOpen={showImageModal}
        onClose={handleCloseImageModal}
        imageData={imageData}
        guid={buildingInfo?.meterGuid}
        timestamp={imageTimestamp}
        buildingName={buildingInfo?.name}
        isLoading={imageLoading}
      />
    </div>
  );
};

export default Profile;
